import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Modal, Typography } from '@orijinworks/frontend-commons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DeleteModalStyles } from '../../Feeds.style';
import { useAttachFormModalHook } from './useAttachFormModalHook';

const formIdEquals = (a, b) => String(a) === String(b);

const FormListItem = React.memo(({ form, selected, onToggle }) => (
  <ListItemButton
    alignItems="flex-start"
    dense
    selected={selected}
    onClick={() => onToggle(form.id)}
    data-form-id={form.id}
  >
    <ListItemIcon sx={{ minWidth: 42 }}>
      <Checkbox
        edge="start"
        checked={selected}
        tabIndex={-1}
        disableRipple
        inputProps={{ 'aria-labelledby': `attach-form-label-${form.id}` }}
      />
    </ListItemIcon>
    <ListItemText
      id={`attach-form-label-${form.id}`}
      primary={form.name || 'Untitled form'}
    />
  </ListItemButton>
));

FormListItem.displayName = 'FormListItem';

const AttachFormModalComponent = ({ open, onClose, onFormConfirm, attachedForms = [] }) => {
  const { forms, loading, error, handleClose } = useAttachFormModalHook(open, onClose);
  const [selectedIds, setSelectedIds] = useState([]);
  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setSelectedIds((attachedForms || []).map((f) => f.id).filter((id) => id != null));
    }
    prevOpenRef.current = open;
  }, [open, attachedForms]);

  const toggleFormId = useCallback((formId) => {
    setSelectedIds((prev) => {
      const has = prev.some((id) => formIdEquals(id, formId));
      if (has) {
        return prev.filter((id) => !formIdEquals(id, formId));
      }
      return [...prev, formId];
    });
  }, []);

  const handleContinue = useCallback(async () => {
    if (typeof onFormConfirm !== 'function') {
      return;
    }
    const selectedForms = forms.filter((f) =>
      selectedIds.some((sid) => formIdEquals(sid, f.id)),
    );
    onFormConfirm(selectedForms);
  }, [onFormConfirm, selectedIds, forms]);

  const selectDisabled = loading || Boolean(error) || forms.length === 0;

  const listContent = useMemo(() => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={32} aria-label="Loading forms" />
        </Box>
      );
    }
    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }
    if (forms.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No forms available.
        </Typography>
      );
    }
    return (
      <List dense disablePadding sx={{ maxHeight: 320, overflow: 'auto' }} role="listbox" aria-multiselectable="true">
        {forms.map((form) => (
          <FormListItem
            key={String(form.id)}
            form={form}
            selected={selectedIds.some((sid) => formIdEquals(sid, form.id))}
            onToggle={toggleFormId}
          />
        ))}
      </List>
    );
  }, [loading, error, forms, selectedIds, toggleFormId]);

  return (
    <Modal
      key={open ? 'attach-form-open' : 'attach-form-closed'}
      isSnackBarShown={false}
      cancelBtnText="Close"
      actionBtnText="Apply"
      heading="Attach forms"
      labelledBy="attach-form-modal-title"
      describedBy="attach-form-modal-description"
      open={open}
      onClose={handleClose}
      onContinue={handleContinue}
      continueButtonProps={{ disabled: selectDisabled }}
      modalType={{ type: 'confirmation', status: 'info', size: 'medium' }}
      showCrossIcon
      styles={{
        mainWrapperStyles: { height: 'auto' },
      }}
      mainModalStyles={DeleteModalStyles}
    >
      <Typography variant="body2" id="attach-form-modal-description" sx={{ mb: 2 }}>
        Select one or more forms to attach. Your previous choices are selected when you reopen this dialog.
      </Typography>
      {listContent}
    </Modal>
  );
};

const AttachFormModal = React.memo(AttachFormModalComponent);

export default AttachFormModal;
